import {
  InteractionRequiredAuthError,
  type AccountInfo,
} from '@azure/msal-browser';

import { apiRequest, loginRequest, msalInstance } from '../config/authConfig';

/**
 * MSAL is the single source of truth for access tokens.
 *
 * `acquireTokenSilent` reads MSAL's own localStorage cache
 * (`cacheLocation: 'localStorage'` in `config/authConfig.ts`) and returns the
 * stored token with no network call, performing the refresh-token grant only
 * when the cached token is missing or within `tokenRenewalOffsetSeconds`
 * (300s by default) of expiring. Don't cache tokens on top of that — a second
 * copy is one nothing invalidates when an API rejects a token, which pins a
 * refused-but-unexpired token for its whole remaining lifetime.
 *
 * This module replaces three separate implementations of the same acquisition
 * logic: `getMslToken`/`useAuth().getAccessToken` (which fell back to a popup
 * on *any* silent failure, so a transient network error opened a popup the
 * browser then blocked for not being user-initiated) and `api/client.ts`'s
 * private `getBffToken`.
 */

/** The token audiences this app requests; each maps to one scope set. */
export type TokenAudience = 'graph' | 'api';

const SCOPES: Record<TokenAudience, string[]> = {
  graph: loginRequest.scopes,
  // Azure AD allows one resource per token, so the API's own app-registration
  // scope is requested separately from the Graph scopes above.
  api: apiRequest.scopes,
};

type PendingAcquisition = {
  promise: Promise<string>;
  forceRefresh: boolean;
};

/**
 * Concurrent callers share one acquisition per audience, so a burst of parallel
 * requests refreshing at the same time triggers a single token request.
 */
const inFlight = new Map<TokenAudience, PendingAcquisition>();

const getAccount = (): AccountInfo | undefined =>
  msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];

const acquireToken = async (
  audience: TokenAudience,
  forceRefresh: boolean
): Promise<string> => {
  const account = getAccount();
  if (!account) {
    throw new Error('User is not authenticated');
  }

  try {
    const { accessToken } = await msalInstance.acquireTokenSilent({
      scopes: SCOPES[audience],
      account,
      forceRefresh,
    });
    return accessToken;
  } catch (error) {
    // Only an interaction-required failure is worth a popup. Any other error
    // (network, config, service) is not something the user can resolve in a
    // dialog, and popups opened outside a user gesture get blocked anyway.
    if (error instanceof InteractionRequiredAuthError) {
      const { accessToken } = await msalInstance.acquireTokenPopup({
        scopes: SCOPES[audience],
        account,
      });
      return accessToken;
    }
    console.error(`Token acquisition failed for "${audience}" scopes:`, error);
    throw error;
  }
};

/**
 * Returns an access token for `audience`, refreshing it when MSAL's cached copy
 * is expired or near expiry.
 *
 * Pass `forceRefresh` only after an API has *rejected* a token (401/403) — it
 * bypasses the cache so the replay sends a genuinely new token rather than the
 * one just refused. See `withAuth` in `api/client.ts`.
 */
export const getToken = async (
  audience: TokenAudience,
  { forceRefresh = false }: { forceRefresh?: boolean } = {}
): Promise<string> => {
  const pending = inFlight.get(audience);

  // Join an in-flight acquisition, unless it would resolve with a token we
  // already know to be stale (a cached read while we need a forced refresh).
  if (pending && (pending.forceRefresh || !forceRefresh)) {
    return pending.promise;
  }

  const promise = acquireToken(audience, forceRefresh).finally(() => {
    if (inFlight.get(audience)?.promise === promise) {
      inFlight.delete(audience);
    }
  });

  inFlight.set(audience, { promise, forceRefresh });
  return promise;
};

/** Access token for Microsoft Graph (SharePoint). */
export const getMslToken = (): Promise<string> => getToken('graph');

/**
 * Access token whose `aud` is our own app registration rather than Microsoft
 * Graph, so the BFF can cryptographically verify the signature.
 */
export const getApiToken = (): Promise<string> => getToken('api');

/**
 * The signed-in user's display name (falling back to their username/email)
 * straight from the MSAL account cache, for non-component code such as
 * `utils/storage.ts`'s `transformToPayload` "createdBy" fallback. Returns
 * undefined when MSAL hasn't initialized or no account is signed in.
 */
export const getCurrentUserDisplayName = (): string | undefined => {
  const account = getAccount();
  return account?.name ?? account?.username;
};

export const logOut = async (): Promise<void> => {
  inFlight.clear();
  try {
    await msalInstance.logoutPopup({
      postLogoutRedirectUri: window.location.origin,
      mainWindowRedirectUri: window.location.origin,
    });
  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  }
};
